import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Pagination,
  Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNotify, usePermissions } from 'react-admin';
import { apiGet, apiDownload, ApiClientError, safeStringify, formatDate, calculatePageCount } from '../utils';

interface AuditLogEntry {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface AuditLogResponse {
  data: AuditLogEntry[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  message?: string;
  environment?: 'development' | 'production';
}

const ACTION_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  'entity:create': 'success',
  'entity:update': 'primary',
  'entity:delete': 'error',
  'entity:verify': 'success',
  'entity:reject': 'warning',
  'entity:request_verification': 'info',
  'entity:bulk_verify': 'success',
  'game_config:create': 'success',
  'game_config:update': 'primary',
  'game_config:delete': 'error',
  'user:login': 'info',
  'user:logout': 'default',
  // Bulk operations - intent
  'bulk:import:intent': 'info',
  'bulk:export:intent': 'info',
  'bulk:update:intent': 'info',
  // Bulk operations - success
  'bulk:import:success': 'success',
  'bulk:export:success': 'success',
  'bulk:update:success': 'success',
  // Bulk operations - failure
  'bulk:import:failure': 'error',
  'bulk:export:failure': 'error',
  'bulk:update:failure': 'error',
  // Legacy bulk operations
  'bulk:import': 'primary',
  'bulk:export': 'primary',
  'bulk:update': 'primary',
};

const JsonDiff = ({ oldValue, newValue }: { oldValue: Record<string, unknown> | null; newValue: Record<string, unknown> | null }) => {
  const [expanded, setExpanded] = useState(false);

  if (!oldValue && !newValue) {
    return <Typography color="textSecondary">No data</Typography>;
  }

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {expanded ? 'Hide Details' : 'Show Details'}
      </Button>
      <Collapse in={expanded}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {oldValue && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Before
              </Typography>
              <Paper
                sx={{
                  p: 1,
                  backgroundColor: '#ffebee',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                <pre style={{ margin: 0 }}>{safeStringify(oldValue)}</pre>
              </Paper>
            </Grid>
          )}
          {newValue && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="success" gutterBottom>
                After
              </Typography>
              <Paper
                sx={{
                  p: 1,
                  backgroundColor: '#e8f5e9',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                <pre style={{ margin: 0 }}>{safeStringify(newValue)}</pre>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Collapse>
    </Box>
  );
};

export const AuditLogViewer = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: 50 });
  const [devModeMessage, setDevModeMessage] = useState<string | null>(null);

  // Filters
  const [actions, setActions] = useState<string[]>([]);
  const [resourceTypes, setResourceTypes] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Selected log for detail view
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [actionsData, resourceTypesData] = await Promise.all([
          apiGet<{ actions: string[] }>('/admin/api/audit-logs/actions'),
          apiGet<{ resourceTypes: string[] }>('/admin/api/audit-logs/resource-types'),
        ]);

        setActions(actionsData.actions);
        setResourceTypes(resourceTypesData.resourceTypes);
      } catch {
        // Ignore errors for filter options
      }
    };

    loadFilterOptions();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiGet<AuditLogResponse>('/admin/api/audit-logs', {
        limit: String(pagination.perPage),
        offset: String((pagination.page - 1) * pagination.perPage),
        action: selectedAction,
        resource_type: selectedResourceType,
        start_date: startDate,
        end_date: endDate,
        q: search,
      });

      setLogs(data.data);
      setPagination((prev) => ({ ...prev, total: data.pagination.total }));
      setDevModeMessage(data.message || null);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'An error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.perPage, selectedAction, selectedResourceType, startDate, endDate, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const filename = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      await apiDownload(`/admin/api/audit-logs/export?${params}`, filename);

      notify('Audit logs exported successfully', { type: 'success' });
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Export failed';
      notify(message, { type: 'error' });
    }
  };

  const canViewAuditLogs =
    permissions?.role === 'admin' ||
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('audit_logs:read');

  if (!canViewAuditLogs) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view audit logs.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Audit Logs</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <IconButton onClick={fetchLogs}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={selectedAction}
                  onChange={(e) => {
                    setSelectedAction(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  label="Action"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  {actions.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={selectedResourceType}
                  onChange={(e) => {
                    setSelectedResourceType(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  label="Resource Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {resourceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                placeholder="Search..."
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography variant="body2" color="textSecondary">
                {pagination.total} entries
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Development Mode Notice */}
      {devModeMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {devModeMessage}
        </Alert>
      )}

      {/* Logs Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : logs.length === 0 && !devModeMessage ? (
        <Alert severity="info">No audit logs found</Alert>
      ) : logs.length === 0 ? null : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {log.userEmail || (log.userId ? log.userId.substring(0, 8) + '...' : 'Unknown')}
                      </Typography>
                      {log.userName && (
                        <Typography variant="caption" color="textSecondary">
                          {log.userName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action}
                        size="small"
                        color={ACTION_COLORS[log.action] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {log.resourceType && (
                        <>
                          <Chip label={log.resourceType} size="small" variant="outlined" />
                          {log.resourceId && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {log.resourceId.substring(0, 8)}...
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {log.ipAddress || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setSelectedLog(log)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={calculatePageCount(pagination.total, pagination.perPage)}
              page={pagination.page}
              onChange={(_, page) => setPagination((prev) => ({ ...prev, page }))}
            />
          </Box>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Audit Log Details
          <Chip
            label={selectedLog?.action}
            size="small"
            color={ACTION_COLORS[selectedLog?.action || ''] || 'default'}
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Timestamp
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedLog.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  User
                </Typography>
                <Typography variant="body1">
                  {selectedLog.userEmail || selectedLog.userId}
                  {selectedLog.userName && ` (${selectedLog.userName})`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Resource
                </Typography>
                <Typography variant="body1">
                  {selectedLog.resourceType || '-'}
                  {selectedLog.resourceId && ` / ${selectedLog.resourceId}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  IP Address
                </Typography>
                <Typography variant="body1">{selectedLog.ipAddress || '-'}</Typography>
              </Grid>
              {selectedLog.userAgent && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    User Agent
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selectedLog.userAgent}
                  </Typography>
                </Grid>
              )}
              {(selectedLog.oldValue || selectedLog.newValue) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Changes
                  </Typography>
                  <JsonDiff oldValue={selectedLog.oldValue} newValue={selectedLog.newValue} />
                </Grid>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Metadata
                  </Typography>
                  <Paper
                    sx={{
                      p: 1,
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    <pre style={{ margin: 0 }}>{safeStringify(selectedLog.metadata)}</pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
